import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FilterModel } from 'ag-grid-enterprise';
import {
  Address,
  ApVendor,
  ClientRegisteredVendor,
  Company,
  CompanyVendor,
  ExemptVendor,
  Vendor
} from 'src/app/classes/models';
import { ApSystemLinkingData } from 'src/app/interfaces/ap-system-linking-data';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { State } from 'src/app/interfaces/state';
import { ApVendorService } from 'src/app/services/ap-vendor.service';
import { ToastService } from 'src/app/services/toast.service';

import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-manage-accounting-links-modal',
  templateUrl: './manage-accounting-links-modal.component.html',
  styleUrls: ['./manage-accounting-links-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ManageAccountingLinksModalComponent implements OnInit {
  constructor(
    public activeModal: NgbActiveModal,
    public ModalService: NgbModal,
    private ApVendorService: ApVendorService,
    private ToastService: ToastService
  ) {}

  @Input() Vendor: Vendor | ExemptVendor | ClientRegisteredVendor | null = null;
  @Input() CompanyVendor!: CompanyVendor;
  @Input() apSystemLinkingData: ApSystemLinkingData | null = null;

  Company!: Company;
  ApVendor: ApVendor | null = null;

  StreetAddress?: Address;
  MailingAddress?: Address;
  apSystemName?: string;
  hideWarningText?: boolean;

  reloadParentData!: () => void;

  suppliedGridQueryData?: { [key: string]: any };

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['link', 'unlink']
  };

  FilterModel: FilterModel = {
    // createdAt: {
    //   filterType: 'date',
    //   type: 'greaterThan',
    //   dateFrom: DateTime.fromJSDate(new Date()).minus({ months: 1 }).toFormat('yyyy-MM-dd HH:mm:ss'),
    //   dateTo: null
    // }
  };

  setFilterModel(
    type:
      'legalName' | 'ein' | 'ssn' | 'dba' | 'StreetAddress' | 'MailingAddress'
  ) {
    if (!this.Vendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Vendor information.',
        { returnError: true }
      );
    }

    const FilterModel: FilterModel = {};

    if (new Set(['legalName', 'dba']).has(type)) {
      Object.assign(FilterModel, {
        [type]: {
          filterType: 'text',
          filter: this.Vendor[type],
          type: 'equals'
        }
      });
    } else if (new Set(['ein', 'ssn']).has(type)) {
      Object.assign(FilterModel, {
        ['fedId']: {
          filterType: 'text',
          filter: this.Vendor[type],
          type: 'equals'
        }
      });
    } else if (type === 'StreetAddress' && this.StreetAddress) {
      Object.assign(FilterModel, {
        ['StreetAddress.address1']: {
          filterType: 'text',
          filter: this.StreetAddress.address1,
          type: 'equals'
        },
        ['StreetAddress.address2']: {
          filterType: 'text',
          filter: this.StreetAddress.address2,
          type: 'equals'
        },
        ['StreetAddress.city']: {
          filterType: 'text',
          filter: this.StreetAddress.city,
          type: 'equals'
        },
        ['StreetAddress.state']: {
          filterType: 'text',
          filter: this.StreetAddress.state,
          type: 'equals'
        },
        ['StreetAddress.zip']: {
          filterType: 'text',
          filter: this.StreetAddress.zip,
          type: 'equals'
        }
      });
    } else if (type === 'MailingAddress' && this.MailingAddress) {
      Object.assign(FilterModel, {
        ['MailingAddress.address1']: {
          filterType: 'text',
          filter: this.MailingAddress.address1,
          type: 'equals'
        },
        ['MailingAddress.address2']: {
          filterType: 'text',
          filter: this.MailingAddress.address2,
          type: 'equals'
        },
        ['MailingAddress.city']: {
          filterType: 'text',
          filter: this.MailingAddress.city,
          type: 'equals'
        },
        ['MailingAddress.state']: {
          filterType: 'text',
          filter: this.MailingAddress.state,
          type: 'equals'
        },
        ['MailingAddress.zip']: {
          filterType: 'text',
          filter: this.MailingAddress.zip,
          type: 'equals'
        }
      });
    }

    this.FilterModel = FilterModel;
  }

  calculatedColumnActions({
    rowData
  }: {
    rowData: any;
  }): GridActionColumnActions[] | null {
    const gridActionColumnActionArr: GridActionColumnActions[] = [];

    const { isLinkedToSpecificVendor } = rowData;

    if (isLinkedToSpecificVendor === 1) {
      gridActionColumnActionArr.push('unlink');
    } else if (isLinkedToSpecificVendor === 0) {
      gridActionColumnActionArr.push('link');
    }

    return gridActionColumnActionArr.length > 0
      ? gridActionColumnActionArr
      : null;
  }

  linkVendorToApVendor(rowData: ApVendor) {
    const ApVendor = rowData;
    if (!ApVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot link Vendor. No ApVendor data found.',
        { returnError: true }
      );
    }

    if (ApVendor.isExempt && !this.CompanyVendor.ExemptVendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot link Vendor. Sorry, this vendor cannot be linked because they are exempt in accounting. Please make sure that the vendor is not exempt in accounting if you wish to link.',
        { returnError: true }
      );
    }

    if (ApVendor.isForced) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot link Vendor. This vendor has a pending change and can not be updated until completed.',
        { returnError: true }
      );
    }

    const isLinked = !!(
      ApVendor.VendorId ||
      ApVendor.ExemptVendorId ||
      ApVendor.ClientRegisteredVendorId
    );

    const confirmLinkVendorModal = this.ModalService.open(
      ConfirmModalComponent,
      { centered: true }
    );

    confirmLinkVendorModal.componentInstance.displayText = isLinked
      ? `This vendor is currently linked to a different VIVE vendor. Linking this vendor will override the current link and will update each accounting system with changes occur. Are you sure you want to do this?`
      : 'This will link the VIVE vendor to the accounting system and will update each system when changes occur. Are you sure that you want to do this?';

    confirmLinkVendorModal.closed.subscribe({
      next: (result) => {
        const attemptToLinkVendor = () => {
          if (result) {
            const apVendorCountQuery = {
              ApConfigId: rowData.ApConfigId
            };

            const ApVendorRecord = {
              id: ApVendor.id,
              syncAt: new Date()
            };

            if (this.CompanyVendor.VendorId) {
              Object.assign(apVendorCountQuery, {
                VendorId: this.CompanyVendor.VendorId
              });
              Object.assign(ApVendorRecord, {
                VendorId: this.CompanyVendor.VendorId,
                ClientRegisteredVendorId: null,
                ExemptVendorId: null
              });
            }

            if (this.CompanyVendor.ExemptVendorId) {
              Object.assign(apVendorCountQuery, {
                ExemptVendorId: this.CompanyVendor.ExemptVendorId
              });
              Object.assign(ApVendorRecord, {
                ExemptVendorId: this.CompanyVendor.ExemptVendorId,
                ClientRegisteredVendorId: null,
                VendorId: null
              });
            }

            if (this.CompanyVendor.ClientRegisteredVendorId) {
              Object.assign(apVendorCountQuery, {
                ClientRegisteredVendorId:
                  this.CompanyVendor.ClientRegisteredVendorId
              });
              Object.assign(ApVendorRecord, {
                ClientRegisteredVendorId:
                  this.CompanyVendor.ClientRegisteredVendorId,
                ExemptVendorId: null,
                VendorId: null
              });
            }

            this.ApVendorService.getCountOfApVendorsLinkedToVendor(
              apVendorCountQuery
            ).subscribe({
              next: (count) => {
                const linkVendor = () => {
                  this.ApVendorService.linkVendor(ApVendorRecord, {
                    id: ApVendor.id
                  }).subscribe({
                    next: () => {
                      this.reloadParentData();
                      this.closeModal();
                    }
                    // Keep commented out for now. Error message is handled by http interceptor.
                    // error: (err) => {
                    //   throw this.ToastService.addErrorToast(
                    //     'Error',
                    //     err.message,
                    //     { returnError: true }
                    //   );
                    // }
                  });
                };

                if (count !== 0) {
                  const confirmMultipleApVendorLinksModal =
                    this.ModalService.open(ConfirmModalComponent, {
                      centered: true
                    });
                  confirmMultipleApVendorLinksModal.componentInstance.displayText =
                    'This Vendor is already linked to an ApVendor within this Accounting System. Are you sure you want to continue?';
                  confirmMultipleApVendorLinksModal.closed.subscribe({
                    next: (result) => {
                      if (result) {
                        linkVendor();
                      }
                    }
                  });
                } else {
                  linkVendor();
                }
              },
              error: (err) => {
                throw this.ToastService.addErrorToast('Error', err.message, {
                  returnError: true
                });
              }
            });
          }
        };

        if (!this.CompanyVendor || !this.CompanyVendor.viveStatus) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Cannot link Vendor. CompanyVendor Record Has No Vive Status.',
            { returnError: true }
          );
        }

        if (
          ['Approved', 'Site Conditional', 'Renewing'].includes(
            this.CompanyVendor.viveStatus
          ) &&
          ApVendor.status === 'Inactive'
        ) {
          const inactiveVendorConfirmModal = this.ModalService.open(
            ConfirmModalComponent,
            { centered: true }
          );
          inactiveVendorConfirmModal.componentInstance.displayText =
            'Are you sure you want to link this VIVE vendor to an Inactive accounting accounting vendor?';
          inactiveVendorConfirmModal.closed.subscribe({
            next: (result) => {
              if (result) {
                attemptToLinkVendor();
              }
            }
          });
        } else {
          attemptToLinkVendor();
        }
      }
    });
  }

  unLinkVendorFromApVendor(rowData: ApVendor) {
    const ApVendor = rowData;

    if (
      !ApVendor.VendorId &&
      !ApVendor.ExemptVendorId &&
      !ApVendor.ClientRegisteredVendorId
    ) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot UnLink Vendor. Sorry, this vendor was not linked and no action will be taken.',
        { returnError: true }
      );
    }

    if (ApVendor.isForced) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot UnLink Vendor. This vendor has a pending change and can not be updated until completed.',
        { returnError: true }
      );
    }

    const unlinkConfirmModal = this.ModalService.open(ConfirmModalComponent, {
      centered: true
    });
    unlinkConfirmModal.componentInstance.displayText =
      'This will unlink the VIVE vendor from the accounting system. Are you sure that you want to do this?';
    unlinkConfirmModal.closed.subscribe({
      next: (result) => {
        if (result) {
          const ApVendorRecord = {
            id: ApVendor.id,
            syncAt: this.apSystemName !== 'CamAcct' ? new Date() : null
          };

          if (this.CompanyVendor.VendorId) {
            Object.assign(ApVendorRecord, { VendorId: null });
          }

          if (this.CompanyVendor.ExemptVendorId) {
            Object.assign(ApVendorRecord, {
              ExemptVendorId: null,
              isExempt: false
            });
          }

          if (this.CompanyVendor.ClientRegisteredVendorId) {
            Object.assign(ApVendorRecord, { ClientRegisteredVendorId: null });
          }

          this.ApVendorService.unLinkVendor(ApVendorRecord, {
            id: ApVendor.id
          }).subscribe({
            next: () => {
              this.reloadParentData();
              this.closeModal();
            }
            // Keep commented out for now. Error message is handled by http interceptor.
            // error: (err) => {
            //   throw this.ToastService.addErrorToast('Error', err.message, {
            //     returnError: true
            //   });
            // }
          });
        }
      }
    });
  }

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    if (action === 'link') {
      this.linkVendorToApVendor(rowData);
    }

    if (action === 'unlink') {
      this.unLinkVendorFromApVendor(rowData);
    }
  }

  close() {
    this.activeModal.dismiss('Cross Click');
  }

  closeModal(result?: any) {
    this.activeModal.close(result);
  }

  ngOnInit(): void {
    if (!this.Vendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No Vendor Record Found.',
        { returnError: true }
      );
    }

    const {
      legalName,
      dba,
      ein,
      ssn,
      phone,
      phone2,
      fax,
      StreetAddress,
      MailingAddress
    } = this.Vendor;

    Object.assign(this, { StreetAddress, MailingAddress });

    const ConfidenceScoreParameters: {
      legalName?: string;
      dba?: string;
      ein?: string;
      ssn?: string;
      phone?: string;
      phone2?: string;
      fax?: string;
      address1?: string;
      address2?: string;
      city?: string;
      state?: State;
      zip?: string;
    } = {
      legalName: legalName || undefined,
      dba: dba || undefined,
      ein: ein || undefined,
      ssn: ssn || undefined,
      phone: phone || undefined,
      phone2: phone2 || undefined,
      fax: fax || undefined
    };

    if (StreetAddress) {
      const { address1, address2, city, state, zip } = StreetAddress;
      Object.assign(ConfidenceScoreParameters, {
        address1: address1 || undefined,
        address2: address2 || undefined,
        city: city || undefined,
        state: state || undefined,
        zip: zip || undefined
      });
    }

    this.suppliedGridQueryData = {
      attributes: ['VendorId', 'ExemptVendorId', 'ClientRegisteredVendorId'],
      parameterizedAttributes: {
        confidence: { parameters: ConfidenceScoreParameters }
      },
      Vendor: {
        attributes: ['legalName'],
        options: { required: false }
      },
      ExemptVendor: {
        attributes: ['legalName'],
        options: { required: false }
      },
      ClientRegisteredVendor: {
        attributes: ['legalName'],
        options: { required: false }
      },
      order: [['confidence', 'DESC']]
    };

    if (this.apSystemLinkingData) {
      const { config, CompanyVendor, ApVendor } = this.apSystemLinkingData;

      if (ApVendor) {
        this.ApVendor = ApVendor;
      }

      if (config) {
        const { apDisplayName, apSystem, id } = config;
        this.apSystemName = apDisplayName || apSystem;
        this.hideWarningText = config.allowCreateVendors;
        Object.assign(this.suppliedGridQueryData, { ApConfigId: id });
      }

      if (CompanyVendor) {
        this.CompanyVendor = CompanyVendor;
        const { VendorId, ExemptVendorId, ClientRegisteredVendorId, Company } =
          this.CompanyVendor;

        if (Company) {
          this.Company = Company;
        }

        const { parameterizedAttributes } = this.suppliedGridQueryData;
        Object.assign(parameterizedAttributes, {
          isLinkedToSpecificVendor: {
            parameters: {
              VendorId: VendorId || undefined,
              ExemptVendorId: ExemptVendorId || undefined,
              ClientRegisteredVendorId: ClientRegisteredVendorId || undefined
            }
          }
        });
      }
    }
  }
}
